import { motion } from "framer-motion";
import { Palette, Type, Ruler, Component, Sparkles } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

const COLORS = [
  ["Cream", "var(--brand-cream)", "#F5F2EC", "bg-cream border border-navy/10"],
  ["Navy", "var(--brand-navy)", "#0A1428", "bg-navy"],
  ["Navy soft", "var(--brand-navy-soft)", "#1A2438", "bg-navy-soft"],
  ["Violet", "var(--brand-violet)", "#6D28D9", "bg-violet"],
  ["Violet deep", "var(--brand-violet-deep)", "#5B21B6", "bg-violet-deep"],
  ["Gold", "var(--brand-gold)", "#C9A24B", "bg-gold"],
];
const SPACES = [["1", "4px"], ["2", "8px"], ["3", "12px"], ["4", "16px"], ["6", "24px"], ["8", "32px"], ["12", "48px"]];
const RADII = [["sm", "6px", "rounded-md"], ["md", "8px", "rounded-lg"], ["lg", "12px", "rounded-xl"], ["pill", "999px", "rounded-full"]];

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-4">
      <h3 className="text-sm font-medium flex items-center gap-2 text-navy/70"><Icon className="w-4 h-4 text-violet" /> {title}</h3>
      {children}
    </section>
  );
}

export default function StyleGuide() {
  const { lang } = useI18n();
  const es = lang === "es";

  return (
    <OSLayout title={es ? "Sistema de diseño" : "Design System"}>
      <div className="space-y-10 max-w-4xl" data-testid="styleguide-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> OMNIA 2.0</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{es ? "Sistema de diseño" : "Design System"}</h2>
          <p className="mt-2 text-navy/50 max-w-xl">{es ? "Tokens y componentes estandarizados. Usa estas clases (btn-primary, badge-violet, field, card-surface) para mantener consistencia visual en todo OMNIA OS." : "Standardized tokens and components. Use these classes (btn-primary, badge-violet, field, card-surface) to keep visual consistency across OMNIA OS."}</p>
        </motion.div>

        <Section icon={Palette} title={es ? "Colores de marca" : "Brand colors"}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sg-colors">
            {COLORS.map(([name, v, hex, cls]) => (
              <div key={name} className="card-surface p-3">
                <div className={`h-16 rounded-md ${cls}`} />
                <div className="mt-2 text-sm font-medium">{name}</div>
                <div className="text-[11px] text-navy/40 font-mono">{hex}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Type} title={es ? "Tipografía" : "Typography"}>
          <div className="card-surface p-6 space-y-3">
            <div className="font-editorial text-5xl tracking-tight">Cormorant Garamond</div>
            <div className="text-xs text-navy/40 font-mono">.font-editorial · H1 display</div>
            <hr className="border-navy/5" />
            <div className="text-2xl font-medium">Satoshi — {es ? "títulos y cuerpo" : "headings & body"}</div>
            <p className="text-navy/60">Satoshi {es ? "es la tipografía base para interfaz." : "is the base UI typeface."} <span className="font-mono text-sm bg-navy/5 px-1.5 py-0.5 rounded">JetBrains Mono</span> {es ? "para datos/código." : "for data/code."}</p>
            <div className="overline text-navy/40">Overline · .overline (0.28em)</div>
          </div>
        </Section>

        <Section icon={Ruler} title={es ? "Espaciado y radios" : "Spacing & radii"}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card-surface p-6">
              <div className="overline text-navy/40 mb-3">{es ? "Espaciado (4px base)" : "Spacing (4px base)"}</div>
              <div className="space-y-2">
                {SPACES.map(([k, px]) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="text-xs text-navy/40 w-16 font-mono">space-{k}</span>
                    <div className="h-3 bg-violet/70 rounded" style={{ width: px }} />
                    <span className="text-[11px] text-navy/40">{px}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-surface p-6">
              <div className="overline text-navy/40 mb-3">{es ? "Radios de borde" : "Border radii"}</div>
              <div className="flex flex-wrap gap-4">
                {RADII.map(([k, px, cls]) => (
                  <div key={k} className="text-center">
                    <div className={`w-16 h-16 bg-violet/15 border border-violet/30 ${cls}`} />
                    <div className="mt-1.5 text-xs font-mono text-navy/50">{k}</div>
                    <div className="text-[10px] text-navy/40">{px}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={Component} title={es ? "Componentes" : "Components"}>
          <div className="card-surface p-6 space-y-6">
            <div>
              <div className="overline text-navy/40 mb-3">{es ? "Botones" : "Buttons"}</div>
              <div className="flex flex-wrap gap-3" data-testid="sg-buttons">
                <button className="btn-primary">btn-primary</button>
                <button className="btn-outline">btn-outline</button>
                <button className="btn-ghost">btn-ghost</button>
                <button className="btn-danger">btn-danger</button>
              </div>
            </div>
            <div>
              <div className="overline text-navy/40 mb-3">Badges</div>
              <div className="flex flex-wrap gap-2" data-testid="sg-badges">
                <span className="badge-violet">violet</span>
                <span className="badge-emerald">emerald</span>
                <span className="badge-gold">gold</span>
                <span className="badge-amber">amber</span>
                <span className="badge-danger">danger</span>
                <span className="badge-muted">muted</span>
              </div>
            </div>
            <div>
              <div className="overline text-navy/40 mb-3">{es ? "Campos" : "Inputs"}</div>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="sg-inputs">
                <input className="field" placeholder=".field" />
                <input className="field-sm" placeholder=".field-sm" />
              </div>
            </div>
          </div>
        </Section>
      </div>
    </OSLayout>
  );
}
